<script setup>
import { computed, ref } from "vue";
import { router } from "@inertiajs/vue3";
import ExpertCard from "@/Components/Brand/sections/ExpertCard.vue";

const props = defineProps({
    // Real public Expert Lists (GH-1310), shaped by ExpertListService: a
    // daily pick of four while categories are parked, or the tabbed pool.
    lists: {
        type: Array,
        default: () => [],
    },
    // Category tabs. Empty while categories are parked for launch
    // (GH-1252): the section then shows every list it is given, untabbed.
    tabs: {
        type: Array,
        default: () => [],
    },
});

// The number of lists shown under the "Featured" (first) tab: one row of
// the desktop grid.
const FEATURED_COUNT = 4;

const hasTabs = computed(() => props.tabs.length > 0);
const activeTab = ref(props.tabs[0] ?? null);

// Red downward triangle used as the select's only dropdown indicator (the
// native arrow is removed via appearance-none).
const selectCaret = `url("data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20width='14'%20height='10'%20viewBox='0%200%2014%2010'%3E%3Cpath%20fill='%23f21439'%20d='M7%2010%200%200h14z'/%3E%3C/svg%3E")`;

// "Featured" shows the most recent lists; the rest filter by the category
// map (config/dashboard.php) keyed off the lowercased tab label.
const visibleLists = computed(() => {
    if (!hasTabs.value) {
        return props.lists;
    }

    if (activeTab.value === props.tabs[0]) {
        return props.lists.slice(0, FEATURED_COUNT);
    }

    const slug = activeTab.value.toLowerCase();

    return props.lists.filter((list) => list.category === slug);
});

// The homepage only renders for guests (members are sent to the
// dashboard), so the bookmark chip leads to login, as on Gift Ideas.
function bookmark() {
    router.visit(route("login"));
}
</script>

<template>
    <section v-if="lists.length" class="pb-14">
        <div
            class="mx-auto max-w-[1400px] rounded-brand bg-brand-purple px-6 py-12 text-white md:px-12 md:py-16"
        >
            <div
                class="flex flex-col items-center gap-6 md:flex-row md:items-end md:justify-between"
            >
                <h2
                    class="text-center font-brand text-[28px] font-bold leading-[30px] md:text-left"
                >
                    Inspiration Experts
                </h2>

                <ul
                    v-if="hasTabs"
                    class="hidden flex-wrap items-center justify-center gap-x-8 gap-y-3 font-brand text-[16px] font-semibold md:flex"
                >
                    <li v-for="tab in tabs" :key="tab">
                        <button
                            type="button"
                            class="pb-0.5 transition"
                            :class="
                                activeTab === tab
                                    ? 'border-b-2 border-brand-red text-white'
                                    : 'text-white/50 hover:text-white'
                            "
                            @click="activeTab = tab"
                        >
                            {{ tab }}
                        </button>
                    </li>
                </ul>
            </div>

            <!-- Mobile: category dropdown (tabs collapse to a select). -->
            <div v-if="hasTabs" class="relative mt-6 md:hidden">
                <select
                    v-model="activeTab"
                    :style="{ backgroundImage: selectCaret }"
                    class="w-full appearance-none rounded-brand bg-white bg-[position:right_1.25rem_center] bg-[length:14px_10px] bg-no-repeat py-3 pl-5 pr-12 font-brand text-[15px] font-semibold text-brand-navy [-moz-appearance:none] [-webkit-appearance:none]"
                >
                    <option v-for="tab in tabs" :key="tab" :value="tab">
                        {{ tab }}
                    </option>
                </select>
            </div>

            <!--
                Cards: a horizontal snap-carousel on mobile (cards peek to hint
                that more exist), switching to a grid from md up.
            -->
            <div
                v-if="visibleLists.length"
                class="-mx-6 mt-8 flex snap-x scroll-px-6 gap-4 overflow-x-auto px-6 pb-2 [-ms-overflow-style:none] [scrollbar-width:none] md:mx-0 md:mt-12 md:grid md:grid-cols-2 md:gap-6 md:overflow-visible md:px-0 md:pb-0 md:scroll-px-0 lg:grid-cols-4 [&::-webkit-scrollbar]:hidden"
            >
                <div
                    v-for="list in visibleLists"
                    :key="list.uuid"
                    class="w-[82%] shrink-0 snap-start md:w-auto md:shrink"
                >
                    <ExpertCard
                        :creator="list.creator"
                        :title="list.title"
                        :description="list.description"
                        :image="list.image"
                        :avatar="list.avatar"
                        :href="list.url"
                        :masthead-photo="list.masthead_photo"
                        :masthead-focus-x="list.masthead_focus_x"
                        :masthead-focus-y="list.masthead_focus_y"
                        expert
                        @bookmark="bookmark"
                    />
                </div>
            </div>

            <p
                v-else
                class="mt-8 rounded-brand border border-dashed border-white/40 px-4 py-10 text-center font-brand text-[15px] text-white/80 md:mt-12"
            >
                No expert lists in this category yet.
            </p>
        </div>
    </section>
</template>
